import { getConfig } from '@/config/config';
import type { DocumentDownload, DocumentDownloadWithFailures, DocumentMetadata, DocumentsMetadataWithFailures, OutputDocumentMetadata } from '@/model/DocumentMetadata';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { Failure, WebApiResponseWithFailures } from './WebApiResponse';
import { webApiResponseBaseQuery } from './webApiResponseBaseQuery';

export type RibRequestPayload = {
  iban: string;
  language: string;
};

export type BaseReportRequestPayload = {
  dateFrom: string;
  dateTo: string;
  language: PossibleValueLang;
  currency: string;
};

export type PortfolioRequestPayload = BaseReportRequestPayload & {
  reportType: 'PORTFOLIO';
};

export type PerfRequestPayload = BaseReportRequestPayload & {
  reportType: 'PERF';
};

export type OperationRequestPayload = BaseReportRequestPayload & {
  reportType: 'OPERATION';
};

export type GenericRequestPayload = OperationRequestPayload | PerfRequestPayload;

export type ReportRequestPayload = PortfolioRequestPayload | GenericRequestPayload;

export type Report = {
  title: string;
  content: string; // base64
};

type DocumentWithFailures = {
  content: {
    file: string;
    fileName: string;
    mediaType?: 'application/pdf';
  };
  failures: Failure[];
};
const prefixUrl = getConfig().prefixUrl ?? '';

export const documentApi = createApi({
  reducerPath: 'documentApi',
  baseQuery: webApiResponseBaseQuery,
  endpoints: (builder) => ({
    getDocumentRibReport: builder.mutation<DocumentWithFailures, { rootId: string; payload: RibRequestPayload }>({
      query: ({ rootId, payload }) => ({
        method: 'POST',
        url: `${prefixUrl}/documents/ribReports`,
        body: payload,
        params: { rootId },
      }),
      transformResponse: (response: WebApiResponseWithFailures<Report>) => ({
        content: {
          file: response?.data[0]?.content ?? '',
          fileName: response?.data[0]?.title ?? '',
        },
        failures: response.failures,
      }),
    }),

    getDocumentPortfolioReport: builder.mutation<DocumentWithFailures, { rootId: string; payload: ReportRequestPayload }>({
      query: ({ rootId, payload }) => ({
        method: 'POST',
        url: `${prefixUrl}/documents/rdps`,
        body: payload,
        params: { rootId },
        timeout: 900_000, // timeout in ms, here 15min
      }),
      transformResponse: (response: WebApiResponseWithFailures<Report>) => ({
        content: {
          file: response?.data[0]?.content ?? '',
          fileName: response?.data[0]?.title ?? '',
        },
        failures: response.failures,
      }),
    }),

    listInputDocumentsMetadata: builder.query<DocumentsMetadataWithFailures<DocumentMetadata>, string>({
      query: (rootId: string) => ({
        method: 'GET',
        url: `${prefixUrl}/documents/metadata`,
        params: {
          rootId,
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<DocumentMetadata>) => ({
        documentsMetadata: response.data,
        failures: response.failures ?? [],
      }),
    }),

    listOutputDocumentsMetadata: builder.query<DocumentsMetadataWithFailures<OutputDocumentMetadata>, string>({
      query: (rootId: string) => ({
        method: 'GET',
        url: `${prefixUrl}/documents/outputs`,
        params: {
          rootId,
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<OutputDocumentMetadata>) => ({
        documentsMetadata: response.data,
        failures: response.failures ?? [],
      }),
    }),

    downloadOutputDocument: builder.query<DocumentDownloadWithFailures, {rootId: string, documentId: string}>({
      query: ({rootId, documentId}) => ({
        method: 'GET',
        url: `${prefixUrl}/documents/downloads/outputs`,
        params: { rootId, documentId },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<DocumentDownload>) => ({
        data: response.data[0],
        failures: response.failures,
      }),
    }),
    downloadInputDocument: builder.query<DocumentDownloadWithFailures, {rootId: string, documentId: string}>({
      query: ({rootId, documentId}) => ({
        method: 'GET',
        url: `${prefixUrl}/documents/downloads/inputs`,
        params: { rootId, documentId },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<DocumentDownload>) => ({
        data: response.data[0],
        failures: response.failures,
      }),
    }),
  }),
});

export const {
  useGetDocumentRibReportMutation,
  useGetDocumentPortfolioReportMutation,
  useListInputDocumentsMetadataQuery,
  useListOutputDocumentsMetadataQuery,
  useDownloadInputDocumentQuery,
  useDownloadOutputDocumentQuery,
} = documentApi;

type PossibleValueLang = 'FR' | 'EN';

export const availableLangs: Lang[] = [
  { intlId: 'fr', value: 'FR' },
  { intlId: 'en', value: 'EN' },
];

export type Lang = { intlId: string; value: PossibleValueLang };
